import axios from 'axios'

import countryToRegion from '@/data/countryToRegion.json'
import regionUserPoolDev from '@/data/region.dev.json'
import regionUserPoolProd from '@/data/region.prod.json'
import timeZoneToCountry from '@/data/timeZoneToCountry.json'
import { queryClient } from '@/lib/reactQuery'

import { API_CONFIG } from './constant/storage'

interface IPInfo {
  country: string
  timezone: string
}

interface UserPoolConfig {
  userPoolId: string
  userPoolDomain: string
  userPoolClientId: string
}

interface APIConfig {
  host: string
  graphql: string
  auth: string
  file: string
  group: string
  userPool: UserPoolConfig
  realtime: string
}

export type TimeZoneKeys = keyof typeof timeZoneToCountry
export type CountryKeys = keyof typeof countryToRegion
export type RegionKeys = 'singapore' | 'ohio' | 'mumbai' | 'tokyo' | 'frankfurt'

export const getTimeZoneyByIP = async () => {
  try {
    const { data } = await queryClient.fetchQuery({
      queryKey: ['ipinfo.io'],
      queryFn: () => axios<IPInfo>('https://ipinfo.io', { timeout: 3000 }),
    })
    return data.timezone
  } catch (e) {
    console.error(e)
  }
  return null
}

export const getTimeZoneLocal = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return timeZone
}

export const getCountryByTimeZone = async () => {
  let timeZone = await getTimeZoneyByIP()
  if (!timeZone) {
    timeZone = getTimeZoneLocal()
  }
  const country = timeZoneToCountry[timeZone as TimeZoneKeys]
  console.debug(`Country: ${country}, TimeZone: ${timeZone}`)
  return country
}

export const getRegionByCountry = (country: CountryKeys) => countryToRegion[country]
export const getUserPoolDev = (region?: RegionKeys | null) => regionUserPoolDev[region ?? 'singapore']
export const getUserPoolProd = (region?: RegionKeys | null) => regionUserPoolProd[region ?? 'ohio']

export const getAPIByRegion = (region: string) => {
  const isProduction = import.meta.env.VITE_ENV === 'PROD'
  const base = import.meta.env.VITE_BASE_URL as string
  const suffix = isProduction ? `.${base}` : `-${base}`
  const userPoolFun = isProduction ? getUserPoolProd : getUserPoolDev

  const graphql = `https://${region}-api${suffix}/graphql`
  const auth = `https://${region}-auth${suffix}`
  const file = `https://${region}-file${suffix}`
  const group = `https://${region}-group${suffix}`
  const realtime = `wss://${region}-api${suffix}/graphql/realtime`
  const host = `${region}-api${suffix}`

  const userPool = userPoolFun(region as RegionKeys)

  return { host, graphql, auth, file, group, userPool, realtime }
}

export const getAPIConfig = async (): Promise<APIConfig> => {
  const cache = localStorage.getItem(API_CONFIG)
  if (cache) {
    return JSON.parse(cache) as APIConfig
  }

  const country = await getCountryByTimeZone()
  const region = getRegionByCountry(country as CountryKeys) as RegionKeys
  console.debug(`Country: ${country}, Region: ${region}`)

  const apiConfig = getAPIByRegion(region)

  localStorage.setItem(API_CONFIG, JSON.stringify(apiConfig))
  return apiConfig
}
